import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PedidoService } from '../../core/services/pedido.service';
import { AlertService } from '../../core/services/alert.service';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';
import { PedidoDetalleApi, PedidoHistorialApi, PedidoListApi } from '../../core/models/api/pedido-api';
import { EstadoPedidoUtility } from '../../core/utility/estado-pedido-utility';

@Component({
    selector: 'app-mis-pedidos',
    standalone: true,
    imports: [CommonModule, UiLoadingComponent],
    template: `
    <main class="max-w-5xl mx-auto px-4 py-8 flex flex-col gap-6">
      <div>
        <h1 class="text-2xl font-black text-gray-800">Mis pedidos</h1>
        <p class="text-sm text-gray-400 mt-1">Historial y estado de tus compras.</p>
      </div>
      <app-ui-loading *ngIf="loading()"></app-ui-loading>
      <div *ngIf="!loading() && !seleccionado()" class="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
              <th class="px-5 py-3">Nro</th>
              <th class="px-5 py-3">Estado</th>
              <th class="px-5 py-3">Total</th>
              <th class="px-5 py-3">Entrega</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let p of items()" (click)="verDetalle(p.uuid)" class="border-b border-gray-50 hover:bg-rose-50/50 cursor-pointer">
              <td class="px-5 py-3 font-black text-gray-800">#{{ p.numeroPedido }}</td>
              <td class="px-5 py-3"><span class="text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-gray-100 text-gray-600">{{ estadoCliente(p) }}</span></td>
              <td class="px-5 py-3 font-bold text-rose-500">S/ {{ p.total.toFixed(2) }}</td>
              <td class="px-5 py-3 text-gray-600">{{ p.fechaEntrega }} · {{ p.rangoHorario }}</td>
            </tr>
          </tbody>
        </table>
        <p *ngIf="items().length === 0" class="px-5 py-8 text-center text-sm text-gray-400">Aún no tienes pedidos.</p>
        <div class="flex items-center justify-between px-5 py-3 border-t border-gray-100 text-sm">
          <button type="button" (click)="pagina(false)" [disabled]="page() <= 1" class="px-4 py-2 rounded-xl border border-gray-200 font-bold text-gray-600 disabled:opacity-40 cursor-pointer">Anterior</button>
          <span class="text-xs text-gray-400">Página {{ page() }} de {{ totalPages() }}</span>
          <button type="button" (click)="pagina(true)" [disabled]="page() >= totalPages()" class="px-4 py-2 rounded-xl border border-gray-200 font-bold text-gray-600 disabled:opacity-40 cursor-pointer">Siguiente</button>
        </div>
      </div>
      <div *ngIf="!loading() && seleccionado() as d" class="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex flex-col gap-4">
        <button type="button" (click)="seleccionado.set(null)" class="self-start text-sm font-bold text-rose-500 hover:underline cursor-pointer">Volver al listado</button>
        <div *ngIf="d.estadoCodigo === 'NO_ENTREGADO'" class="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-center">
          <p class="text-sm font-black text-gray-800">Este pedido ha vuelto al taller.</p>
          <p class="text-xs text-gray-500 mt-1">No pudimos entregarlo. Contáctanos para coordinar una nueva entrega.</p>
        </div>
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h2 class="text-lg font-black text-gray-800">Pedido #{{ d.numeroPedido }}</h2>
          <span class="text-xs font-bold uppercase px-3 py-1 rounded-full bg-rose-50 text-rose-500">{{ estadoCliente(d) }}</span>
        </div>
        <p class="text-sm text-gray-600">{{ d.fechaEntrega }} · {{ d.rangoHorario }} · Total S/ {{ d.total.toFixed(2) }}</p>
        <p *ngIf="d.ventaSerie && d.ventaNumero" class="text-sm text-gray-600"><span class="font-bold">Comprobante electrónico:</span> {{ d.ventaNumero }}</p>
        <ul class="flex flex-col gap-1 text-sm text-gray-700">
          <li *ngFor="let it of d.items">{{ it.nombre }} × {{ it.cantidad }} — S/ {{ it.precioTotal.toFixed(2) }}</li>
        </ul>
        <div>
          <p class="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Historial</p>
          <ol class="flex flex-col gap-2">
            <li *ngFor="let h of historialCliente(d)" class="flex gap-3 text-xs">
              <span class="text-gray-400 whitespace-nowrap">{{ h.createdAt | date:'short' }}</span>
              <span class="text-gray-700"><span class="font-bold">{{ h.estado }}</span><span *ngIf="h.comentario"> · {{ h.comentario }}</span></span>
            </li>
          </ol>
        </div>
      </div>
    </main>
    `
})
export class MisPedidosComponent implements OnInit {
    private pedidos = inject(PedidoService);
    private alerts = inject(AlertService);

    loading = signal(true);
    items = signal<PedidoListApi[]>([]);
    seleccionado = signal<PedidoDetalleApi | null>(null);
    page = signal(1);
    total = signal(0);
    pageSize = 9;

    ngOnInit(): void {
        this.cargar();
    }

    estadoCliente(p: { estadoCodigo: string; estado: string }): string {
        return EstadoPedidoUtility.nombreCliente(p.estadoCodigo, p.estado);
    }

    historialCliente(p: PedidoDetalleApi): PedidoHistorialApi[] {
        return EstadoPedidoUtility.historialCliente(p.historial);
    }

    totalPages(): number {
        return Math.max(1, Math.ceil(this.total() / this.pageSize));
    }

    pagina(next: boolean) {
        this.page.update((p) => next ? p + 1 : Math.max(1, p - 1));
        this.cargar();
    }

    verDetalle(uuid: string) {
        this.loading.set(true);
        this.pedidos.getPedido(uuid).subscribe({
            next: (d) => {
                this.seleccionado.set(d);
                this.loading.set(false);
            },
            error: (err) => {
                this.loading.set(false);
                this.alerts.error(err?.message || 'No se pudo cargar el pedido.');
            }
        });
    }

    private cargar() {
        this.loading.set(true);
        this.pedidos.listMisPedidos(this.page(), this.pageSize).subscribe({
            next: (res) => {
                this.items.set(res.data);
                this.total.set(res.total);
                this.loading.set(false);
            },
            error: (err) => {
                this.loading.set(false);
                this.alerts.error(err?.message || 'No se pudieron cargar tus pedidos.');
            }
        });
    }
}
